import Cookies from "js-cookie";
import { useEffect, useState } from "react";

import { validateAuthToken } from "../api";

const cachedScripts = [];
function useScript(src, options = {}) {
  const { integrity, crossOrigin } = options;
  const [state, setState] = useState({
    loaded: false,
    error: false,
  });

  useEffect(() => {
    if (cachedScripts.includes(src)) {
      setState({
        loaded: true,
        error: false,
      });
    } else {
      cachedScripts.push(src);

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      if (integrity) {
        script.integrity = integrity;
      }
      if (crossOrigin) {
        script.crossOrigin = crossOrigin;
      }

      const onScriptLoad = () => {
        setState({
          loaded: true,
          error: false,
        });
      };

      const onScriptError = () => {
        const index = cachedScripts.indexOf(src);
        if (index >= 0) cachedScripts.splice(index, 1);
        script.remove();

        setState({
          loaded: true,
          error: true,
        });
      };

      script.addEventListener("load", onScriptLoad);
      script.addEventListener("error", onScriptError);

      document.body.appendChild(script);

      return () => {
        script.removeEventListener("load", onScriptLoad);
        script.removeEventListener("error", onScriptError);
      };
    }
  }, [src, integrity, crossOrigin]);

  return [state.loaded, state.error];
}

function useWindowSize() {
  const isClient = typeof window === "object";

  function getSize() {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined,
    };
  }

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    if (!isClient) {
      return false;
    }

    function handleResize() {
      setWindowSize(getSize());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient]); // Empty array ensures that effect is only run on mount and unmount

  return windowSize;
}

function useIsMobile() {
  const { width } = useWindowSize();
  return width < 480;
}

function useAuthStatus() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isValidatingAuth, setIsValidatingAuth] = useState(true);

  useEffect(() => {
    if (!isValidatingAuth) return;

    const validate = async () => {
      try {
        await validateAuthToken();
        setIsAuthenticated(true);
      } catch {
        Cookies.remove("token");
      }
      setIsValidatingAuth(false);
    };
    validate();
  }, [isValidatingAuth]);

  return [isAuthenticated, isValidatingAuth];
}

export { useScript, useWindowSize, useIsMobile, useAuthStatus };
